import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from './store';

export type MediaData = {
  assetId: string;
  mediaType: 'image' | 'video';
  mediaSrc: string;
  name?: string;
  source?: 'default' | 'sharing-my-files' | 'sharing-shared-with-me';
  groupId?: string;
};

export type InviteModalData = {
  isOpen: boolean;
  assetId?: string;
  mode?: 'share' | 'invite';
};

export type commonStateType = {
  isFromPhoto?: boolean;
  selectedMedia?: MediaData | null;
  inviteModal?: InviteModalData;
  switchedProfileUserId?: string | null;
  focusedFamilyTreePersonId?: string | null;
};

const initialState: commonStateType = {
  isFromPhoto: false,
  selectedMedia: null,
  inviteModal: {
    isOpen: false,
    assetId: undefined,
    mode: 'share'
  },
  switchedProfileUserId: null,
  focusedFamilyTreePersonId: null
};

export const commonStateSlice = createSlice({
  name: 'commonStateSlice',
  initialState,
  reducers: {
    setCommonState: (state, action) => {
      const { isFromPhoto } = action.payload;
      state.isFromPhoto = isFromPhoto;
      return state;
    },
    setSelectedMedia: (state, action) => {
      state.selectedMedia = action.payload;
      return state;
    },
    clearSelectedMedia: state => {
      state.selectedMedia = null;
      return state;
    },
    setInviteModal: (state, action) => {
      state.inviteModal = action.payload;
      return state;
    },
    openInviteModal: (state, action) => {
      state.inviteModal = {
        isOpen: true,
        assetId: action.payload?.assetId,
        mode: action.payload?.mode || 'share'
      };
      return state;
    },
    closeInviteModal: state => {
      state.inviteModal = {
        isOpen: false,
        assetId: undefined,
        mode: 'share'
      };
      return state;
    },
    setSwitchedProfileUserId: (state, action) => {
      state.switchedProfileUserId = action.payload;
      return state;
    },
    setFocusedFamilyTreePersonId: (state, action) => {
      state.focusedFamilyTreePersonId = action.payload;
      return state;
    },
    resetCommonState: () => {
      return initialState;
    }
  }
});

export const {
  setCommonState,
  setSelectedMedia,
  clearSelectedMedia,
  setInviteModal,
  openInviteModal,
  closeInviteModal,
  setSwitchedProfileUserId,
  setFocusedFamilyTreePersonId
} = commonStateSlice.actions;

export const getCommonState = (state: RootState) => state.commonStateSlice;
export const getInviteModal = (state: RootState) =>
  state.commonStateSlice.inviteModal;
export const getIsInviteModalOpen = (state: RootState) =>
  state.commonStateSlice.inviteModal.isOpen;
export const getInviteAssetId = (state: RootState) =>
  state.commonStateSlice.inviteModal.assetId;
export const getInviteMode = (state: RootState) =>
  state.commonStateSlice.inviteModal.mode;
export const getSwitchedProfileUserId = (state: RootState) =>
  state.commonStateSlice.switchedProfileUserId;
export const getFocusedFamilyTreePersonId = (state: RootState) =>
  state.commonStateSlice.focusedFamilyTreePersonId;
